import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import queryString from "query-string";
import {
  toWeekStartDate,
  toWeekEndDate,
  toMonthStartDate,
  toMonthEndDate,
  toMonthCalendarStartDate,
  toMonthCalendarEndDate,
} from "../../helpers/moment";

import { POST, PUT, newFetch } from "../../helpers/fetch";
import { config } from "../../config/index";

import SiteSelect from "../../components/SmartScheduler/SiteSelect";
import Calendar from "../../components/SmartScheduler/Calendar";
import TotalHours from "../../components/SmartScheduler/TotalHours";
import ShiftPreference from "../../components/SmartScheduler/ShiftPreference";
import AssignedShift from "../../components/SmartScheduler/AssignedShift";
import ApplyShift from "../../components/SmartScheduler/ApplyShift";
import PulseSurvey from "../../components/SmartScheduler/PulseSurvey";
import ToastUtil from "../../components/ToastNotification/ToastUtil";
import { distanceTo, isInsideCircle } from "geofencer";

const { API_URL } = config;

const SmartScheduler = ({
  client,
  sites,
  activeSite,
  setActiveSite,
  location: { search },
}) => {
  const params = queryString.parse(search);
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  /**
   * States
   */
  // Calendar State
  const [activeView, setActiveView] = useState("week");
  //const [activeDate, setActiveDate] = useState(moment())
  const [activeDate, setActiveDate] = useState(moment(params.date));
  const [activeWeek, setActiveWeek] = useState({});
  const [activeMonth, setActiveMonth] = useState({});

  const [siteSchedules, setSiteSchedules] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [activeSchedules, setActiveSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [attendanceStatuses, setAttendanceStatuses] = useState([]);
  const [showAllShifts, setShowAllShifts] = useState(false);
  const [siteShifts, setSiteShifts] = useState([]);
  const [activeShifts, setActiveShifts] = useState([]);
  const [preferredShifts, setPreferredShifts] = useState([]);

  const [totalHours, setTotalHours] = useState(0);
  const [updateTime, setUpdateTime] = useState(moment());
  const dateRef = useRef(null);

  const [circleRadius, setCircleRadius] = useState({});
  /**
   * Handler Functions
   */
  const changeSiteHandler = (e) => setActiveSite(e.target.value);
  const toggleViewHandler = (view) =>
    activeView !== view && setActiveView(view);

  // Get client site circle radius
  useEffect(() => {
    if (activeSite) {
      const selectedSite = filteredSites();
      if (selectedSite.length >= 0) {
        if (selectedSite[0] !== undefined) {
          const { lat, lng, distance } = selectedSite[0];

          const circle = {
            center: [Number(lat), Number(lng)], // Sample Location: Tanjong Pagar, Gouco Tower
            radius: Number(distance), // in km
          };

          setCircleRadius(circle);
        }
      }
    }
  }, [activeSite]);

  // change active week
  useEffect(() => {
    if (client) {
      setActiveWeek({
        startDate: toWeekStartDate(moment(activeDate), client.start_day),
        endDate: toWeekEndDate(moment(activeDate), client.start_day),
      });

      setActiveMonth({
        startDate: toMonthStartDate(moment(activeDate)),
        endDate: toMonthEndDate(moment(activeDate)),
        calendarStartDate: toMonthCalendarStartDate(
          moment(activeDate),
          client.start_day
        ),
        calendarEndDate: toMonthCalendarEndDate(
          moment(activeDate),
          client.start_day
        ),
      });
    }
  }, [activeDate, client]);

  useEffect(() => {
    const fetchSiteSchedules = async () => {
      if (activeSite && activeWeek) {
        let url = `${API_URL}/site/${activeSite}/schedule`;

        try {
          let response = await fetch(url, {
            mode: "cors",
            method: "POST",
            withCredentials: true,
            body: JSON.stringify({
              start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
              end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
            }),
            headers: {
              Authorization: token,
              "Access-Control-Allow-Origin": "*",
              "Content-Type": "application/json; charset=utf-8",
            },
          });

          let data = await response.json();
          if (data.success) {
            let schedulesData = data.data;
            const currentWeekStartDate = toWeekStartDate(moment(), client.start_day);

            if (
              //added client for enable br upload warning
              client &&
              client.enable_br_upload === false &&
              moment(activeWeek.startDate).isSameOrAfter(
                moment(currentWeekStartDate)
              ) &&
              moment(activeWeek.startDate).isSameOrBefore(
                toWeekEndDate(
                  moment().add(1, "weeks"),
                  client.start_day
                ).format("YYYY-MM-DD")
              )
            ) {
              if (
                schedulesData.length === 0 &&
                dateRef.current !==
                  moment(activeWeek.startDate).format("YYYY-MM-DD")
              ) {
                dateRef.current = moment(activeWeek.startDate).format(
                  "YYYY-MM-DD"
                );
                const api_path = `/site/${activeSite}/schedule/create`;
                const resp = await newFetch(POST, api_path, {
                  start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
                  end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
                  activeDate,
                });

                if (resp.success) {
                  schedulesData = resp.scheduleArr;
                }
                const filtered = schedulesData.reduce(
                  (result, option) =>
                    option.date === moment(activeDate).format("YYYY-MM-DD") &&
                    option.shift.site_id === activeSite
                      ? result.push({
                          schedule_id: option.id,
                          date: option.date,
                          end_time: option.shift.end_time,
                          shift: option.shift.name,
                          shift_id: option.shift_id,
                          site_id: option.shift.site_id,
                          start_time: option.shift.start_time,
                          //status: 1,
                          //...option.shift,
                        }) && result
                      : result,
                  []
                );
                setActiveSchedules(filtered);
              }
            }
            setSiteSchedules(schedulesData);
          } else {
            ToastUtil.error(data.message);
          }
        } catch (error) {
          ToastUtil.error(error);
        }
      }
    };

    fetchSiteSchedules();
  }, [activeWeek, activeSite, updateTime]);

  // Fetch Site Shifts
  useEffect(() => {
    const fetchSiteShifts = async () => {
      let url = `${API_URL}/site/${activeSite}/shifts`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let shiftsData = data.data.sort(
            (a, b) =>
              moment(a.start_time, "HH:mm:ss") -
              moment(b.start_time, "HH:mm:ss")
          );
          // if (activeSite === 1)
          //   shiftsData = moment(activeDate).isBefore(moment("2020-04-19"))
          //     ? shiftsData.filter((x) => [1, 2, 3, 7].includes(x.id))
          //     : shiftsData.filter((x) => x.id > 3);

          setSiteShifts(shiftsData);
        } else {
          ToastUtil.error(data.message);
        }
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchSiteShifts();
  }, [activeSite, activeDate]);

  // Fetch User Preferred Shifts
  useEffect(() => {
    const fetchShiftPreference = async () => {
      let url = `${API_URL}/associate/sites/${activeSite}/preference`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        if (data.success) {
          let filtered = data.data
            .filter(
              (preference) =>
                preference.date === moment(activeDate).format("YYYY-MM-DD") &&
                preference.shift.site_id === activeSite
            )
            .sort(
              (a, b) =>
                moment(a.shift.start_time, "HH:mm:ss") -
                moment(b.shift.start_time, "HH:mm:ss")
            );
          setPreferredShifts(filtered);
        } else {
          ToastUtil.error(data.message);
        }
        // data.success ? setSiteShifts(data.data) : ToastUtil.error(data.message)
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    if (activeSite) fetchShiftPreference();
  }, [activeDate, activeSite, updateTime]);

  // Fetch Schedule
  useEffect(() => {
    const fetchSchedule = async () => {
      if (client) {
        if (client.enable_br_upload === false) {
          if (activeSite !== null) {
            let api_path = `/associate/site/${activeSite}/myschedule2`;
            try {
              const response = await newFetch(PUT, api_path, {
                start_date: moment(activeWeek.startDate).format("YYYY-MM-DD"),
                end_date: moment(activeWeek.endDate).format("YYYY-MM-DD"),
                activeDate,
              });
              if (response.data) {
                setSchedules(response.data);
              }
            } catch (error) {
              ToastUtil.error(error);
            }
          }
        } else {
          let url = `${API_URL}/associate/myschedule`;
          try {
            let response = await fetch(url, {
              mode: "cors",
              method: "GET",
              withCredentials: true,
              headers: {
                Authorization: token,
                "Access-Control-Allow-Origin": "*",
                "Content-Type": "application/json charset=utf-8",
              },
            });

            let data = await response.json();
            if (data.success) {
              const scheduleBySite = data.data.filter(
                (x) => x.site_id === activeSite
              );
              setSchedules(scheduleBySite);
            } else {
              ToastUtil.error(data.message);
            }
          } catch (error) {
            ToastUtil.error(error);
          }
        }
      }
    };
    fetchSchedule();
  }, [activeSite, activeWeek]); //TODO: optimize this for the future

  // Active Schedule
  useEffect(() => {
    let clone = JSON.parse(JSON.stringify(schedules));
    let filtered = clone.filter(
      (schedule) => schedule.date === moment(activeDate).format("YYYY-MM-DD")
    );

    if (client && client.enable_br_upload === false) {
      let has_schedule = filtered.some(
        (schedule) => schedule.has_schedule === 1
      );
      setShowAllShifts(!has_schedule);
    }

    if (filtered.length > 0) {
      setActiveSchedules(filtered);
    } else {
      setActiveSchedules([]);
    }
  }, [activeDate, activeSite, schedules, updateTime]);

  useEffect(() => {
    const fetchTotalHours = async () => {
      let api_path = `/site/${activeSite}/associate/${user.id}/totalWorkHours`;
      let body = { date: moment(activeDate).format("YYYY-MM-DD") };
      let response = await newFetch(POST, api_path, body);

      if (response.success) {
        setTotalHours(response.data);
      } else {
        setTotalHours(0);
      }
    };

    if (activeSite && activeDate) fetchTotalHours();
  }, [activeSite, activeDate]);

  // Fetch Statuses
  useEffect(() => {
    const fetchStatuses = async () => {
      let url = `${API_URL}/attendanceStatus`;

      try {
        let response = await fetch(url, {
          mode: "cors",
          method: "GET",
          withCredentials: true,
          headers: {
            Authorization: token,
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json charset=utf-8",
          },
        });

        let data = await response.json();

        data.success ? setAttendanceStatuses(data.data) : ToastUtil.error(data.message);
      } catch (error) {
        ToastUtil.error(error);
      }
    };

    fetchStatuses();
  }, []);

  const filteredSites = () => sites.filter((item) => item.id === activeSite);

  return (
    <React.Fragment>
      <div className="container pt-4">
        <h4 className="text-brand">Smart Scheduler</h4>
        <SiteSelect
          client={client}
          sites={sites}
          activeSite={activeSite}
          changeSite={changeSiteHandler}
        />
      </div>
      <Calendar
        activeView={activeView}
        // shifts={shifts}
        toggleViewHandler={toggleViewHandler}
        activeDate={activeDate}
        setActiveDate={setActiveDate}
        activeWeek={activeWeek}
        setActiveWeek={setActiveWeek}
        activeMonth={activeMonth}
        setActiveMonth={setActiveMonth}
        client={client}
        activeSite={activeSite}
      />
      {activeView === "week" && (
        <TotalHours
          activeClient={client}
          activeDate={activeDate}
          activeWeek={activeWeek}
          totalHours={totalHours}
          activeSchedules={activeSchedules}
        />
      )}
      <AssignedShift
        activeDate={activeDate}
        activeSchedules={activeSchedules}
        activeSite={activeSite}
        selectedSchedule={selectedSchedule}
        setSelectedSchedule={setSelectedSchedule}
        activeClient={client}
        circleRadius={circleRadius}
        isInsideCircle={isInsideCircle}
        distanceTo={distanceTo}
        showAllShifts={showAllShifts}
      />
      {!activeDate || !client ? null : siteSchedules &&
        siteSchedules.length > 0 &&
        moment(activeDate)
          .startOf("day")
          .isSameOrAfter(moment().startOf("day")) ? (
        <React.Fragment>
          <ApplyShift
            setActiveDate={setActiveDate}
            siteSchedules={siteSchedules}
            activeDate={activeDate}
            activeSite={activeSite}
            siteShifts={siteShifts}
            activeSchedules={activeSchedules}
            client={client}
          />
        </React.Fragment>
      ) : null}
      <ShiftPreference
        activeClient={client}
        weekSchedules={siteSchedules}
        setUpdateTime={setUpdateTime}
        activeDate={activeDate}
        activeSite={activeSite}
        siteShifts={siteShifts}
        preferredShifts={preferredShifts}
      />

      {selectedSchedule &&
      (selectedSchedule.status === 2 ||
        selectedSchedule.status === 3 ||
        selectedSchedule.status === 4) &&
      moment().isAfter(
        moment(
          `${selectedSchedule.date} ${selectedSchedule.end_time}`,
          "YYYY-MM-DD HH:mm:ss"
        )
      ) ? (
        client && client.enable_pulse_survey ? (
          <PulseSurvey client={client} activeSchedule={selectedSchedule} />
        ) : null
      ) : null}
    </React.Fragment>
  );
};

export default SmartScheduler;
